import { compact, keyBy } from 'lodash';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';

import type { EntityAssetOptionGroup } from '../hooks/useHouseholdAssetsForCrossEntityDp';

/**
 * Resolve composite asset values to rich option objects, preferring the
 * inline-options copy of any duplicated value over the household-pool copy.
 *
 * `keyBy` keeps the LAST match for a given key, so we put `inlineOptions`
 * after `householdOptions` in the spread. Inline options carry per-entity
 * rendering details (e.g. the "Selected entity's …" group rename) that
 * the bare household-pool entries don't have. The household pool excludes
 * the current entity, so current-entity picks resolve via `inlineOptions`.
 */
export function resolveSelectedAssetOptions(
  compositeValues: string[],
  inlineOptions: TypeaheadSelectInputOption<string>[],
  householdOptions: TypeaheadSelectInputOption<string>[]
): TypeaheadSelectInputOption<string>[] {
  const optionsByValue = keyBy(
    [...householdOptions, ...inlineOptions],
    'value'
  );
  return compact(compositeValues.map((v) => optionsByValue[v]));
}

/**
 * Builds the option groups for the "View all assets to distribute" drawer. The
 * group for the entity that makes the DP is always first, before the groups of
 * other holders in the household. The drawer omits that group when the entity
 * holds no assets.
 */
export function buildDrawerAssetGroups(
  currentEntityGroup: EntityAssetOptionGroup,
  otherHolderGroups: EntityAssetOptionGroup[]
): EntityAssetOptionGroup[] {
  if (currentEntityGroup.options.length === 0) return otherHolderGroups;
  return [currentEntityGroup, ...otherHolderGroups];
}

/**
 * Orders options so that the selected options come first in each group. An
 * account parent row and its child rows move together, and only when all the
 * children are selected. The groups keep their original order, and each group
 * stays contiguous, which MUI Autocomplete `groupBy` requires.
 */
export function moveSelectedOptionsToGroupTop(
  options: TypeaheadSelectInputOption<string>[],
  selectedValues: string[]
): TypeaheadSelectInputOption<string>[] {
  const selected = new Set(selectedValues);

  // A block is a top-level option followed by its child rows. The option
  // builders put each child row directly after its parent row.
  const blocks: TypeaheadSelectInputOption<string>[][] = [];
  for (const option of options) {
    const lastBlock = blocks.at(-1);
    if (
      lastBlock &&
      option.parentValue != null &&
      lastBlock[0]?.value === option.parentValue
    ) {
      lastBlock.push(option);
    } else {
      blocks.push([option]);
    }
  }

  const isBlockSelected = (head: TypeaheadSelectInputOption<string>) => {
    if (!head.isParentRow) return selected.has(head.value);
    const children = head.childValues ?? [];
    return children.length > 0 && children.every((c) => selected.has(c));
  };

  const blocksByGroup = new Map<
    string | undefined,
    {
      selected: TypeaheadSelectInputOption<string>[][];
      unselected: TypeaheadSelectInputOption<string>[][];
    }
  >();
  for (const block of blocks) {
    const head = block[0];
    if (!head) continue;
    let group = blocksByGroup.get(head.groupName);
    if (!group) {
      group = { selected: [], unselected: [] };
      blocksByGroup.set(head.groupName, group);
    }
    if (isBlockSelected(head)) {
      group.selected.push(block);
    } else {
      group.unselected.push(block);
    }
  }

  return Array.from(blocksByGroup.values()).flatMap((group) =>
    [...group.selected, ...group.unselected].flat()
  );
}

/**
 * Normalizes the multi-select's new value list after a change, handling the
 * synthetic account "parent" rows. Because a parent's value is never stored in
 * the selection, MUI always reports a parent click as an "add", so we toggle
 * based on the children's *current* selection state: if all children are
 * already selected we deselect them, otherwise we select them all. Parent
 * values are always stripped from the result so only real asset values persist.
 */
export function applyAccountCascade(
  nextValues: string[],
  parentOptions: TypeaheadSelectInputOption<string>[]
): string[] {
  const parentByValue = keyBy(parentOptions, 'value');
  // Snapshot of the real (non-parent) selection. Each parent's "are all my
  // children already selected?" decision reads this snapshot rather than the
  // mutating `result`, so when a change event carries more than one parent, one
  // parent's cascade can't skew another's toggle direction.
  const baseSelection = new Set(nextValues.filter((v) => !parentByValue[v]));
  const result = new Set(baseSelection);
  for (const value of nextValues) {
    const parent = parentByValue[value];
    if (!parent) continue;
    const children = parent.childValues ?? [];
    const allSelected =
      children.length > 0 && children.every((c) => baseSelection.has(c));
    for (const child of children) {
      if (allSelected) {
        result.delete(child);
      } else {
        result.add(child);
      }
    }
  }
  return Array.from(result);
}

/**
 * Read-side conversion: expands any selected account parent (`assetGroup:<id>`)
 * into its child statics so the multiselect renders the holdings (and the
 * derived parent checkbox) as selected. The selection is then represented
 * purely as leaf statics internally, which keeps the cascade and the
 * checked/indeterminate logic unchanged. Values that aren't account parents
 * (manual statics, policies, stakes, legacy group selections) pass through.
 */
export function expandSelectedGroupsToStatics(
  values: string[],
  parentOptions: TypeaheadSelectInputOption<string>[]
): string[] {
  const parentByValue = keyBy(parentOptions, 'value');
  const result: string[] = [];
  const seen = new Set<string>();
  const add = (value: string) => {
    if (seen.has(value)) return;
    seen.add(value);
    result.push(value);
  };
  for (const value of values) {
    const parent = parentByValue[value];
    if (parent) {
      for (const child of parent.childValues ?? []) add(child);
    } else {
      add(value);
    }
  }
  return result;
}

/**
 * Write-side conversion: when every holding of an account is selected, collapse
 * those child statics into the account's `assetGroup:<id>` so the DP persists a
 * single group reference (resolved live to the account's members) rather than a
 * frozen list of static IDs. A partially-selected account keeps its individual
 * `assetStatic` values. Inverse of {@link expandSelectedGroupsToStatics}.
 */
export function collapseStaticsToGroups(
  values: string[],
  parentOptions: TypeaheadSelectInputOption<string>[]
): string[] {
  const selected = new Set(values);
  for (const parent of parentOptions) {
    const children = parent.childValues ?? [];
    if (children.length > 0 && children.every((c) => selected.has(c))) {
      for (const child of children) selected.delete(child);
      selected.add(parent.value);
    }
  }
  return Array.from(selected);
}

/**
 * Filters asset options by the typeahead query while keeping account parents
 * and their holdings visible together: a matched child keeps its parent row,
 * and a matched parent keeps all its child rows. Operates on values (not object
 * identity) so the pinned "Selected" copies and home-group copies of an option
 * are both retained.
 */
export function filterAssetOptionsWithAccounts(
  options: TypeaheadSelectInputOption<string>[],
  state: { inputValue: string }
): TypeaheadSelectInputOption<string>[] {
  const query = state.inputValue.trim().toLowerCase();
  if (!query) return options;

  const keepValues = new Set<string>();
  for (const option of options) {
    if (!option.display.toLowerCase().includes(query)) continue;
    keepValues.add(option.value);
    if (option.parentValue != null) keepValues.add(option.parentValue);
    for (const child of option.childValues ?? []) keepValues.add(child);
  }

  return options.filter((option) => keepValues.has(option.value));
}
